import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTable, useFilters, useSortBy } from 'react-table';
import { matchSorter } from 'match-sorter';
import { get } from 'lodash';
import colors from '../ColorMap/ColorMap';

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

function Table({
  columns,
  data,
  getHeaderProps,
  tableClass,
  filterOptions,
  rowKey,
  getCustomRowProps,
  cellClassName,
}) {
  const filterTypes = useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,
    }),
    []
  );

  const arrowBorderStyle = '4px solid transparent';
  const arrowContainerClassnames = 'inline-block w-0 h-0';
  const getArrowColor = (isActive) => {
    return `6px solid ${isActive ? colors['color-blue-100'] : colors['color-gray-80']}`;
  };

  const ArrowUp = ({ isActive }) => {
    return (
      <div
        className={arrowContainerClassnames}
        style={{
          borderLeft: arrowBorderStyle,
          borderRight: arrowBorderStyle,
          borderBottom: getArrowColor(isActive),
        }}
      />
    );
  };

  ArrowUp.propTypes = {
    isActive: PropTypes.bool.isRequired,
  };

  const ArrowDown = ({ isActive }) => (
    <div
      className={arrowContainerClassnames}
      style={{
        borderLeft: arrowBorderStyle,
        borderRight: arrowBorderStyle,
        borderTop: getArrowColor(isActive),
      }}
    />
  );

  ArrowDown.propTypes = {
    isActive: PropTypes.bool.isRequired,
  };

  const getSortIcon = (column) => {
    return (
      <div className="flex flex-col items-center ml-1 space-y-[2px]">
        <ArrowUp isActive={column.isSorted && !column.isSortedDesc} />
        <ArrowDown isActive={column.isSorted && column.isSortedDesc} />
      </div>
    );
  };

  const tableInstance = useTable(
    {
      columns,
      data,
      filterTypes,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setFilter } =
    tableInstance;

  useEffect(() => {
    Object.keys(filterOptions).forEach((column) => {
      if (Object.prototype.hasOwnProperty.call(filterOptions, column)) {
        setFilter(column, filterOptions[column][0]);
      }
    });
  }, [filterOptions, setFilter]);

  return (
    <>
      <table
        {...getTableProps()}
        className={`min-w-full divide-y divide-gray-200 ${tableClass}`}
      >
        <thead className="bg-vartana-gray-30">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                return (
                  <th
                    {...column.getHeaderProps([
                      getHeaderProps(column),
                      column.getSortByToggleProps(),
                    ])}
                    scope="col"
                  >
                    <div
                      className={`flex items-center ${
                        column.canSort ? 'cursor-pointer' : ''
                      } select-none`}
                    >
                      {column.render('Header')}
                      <span className="ml-2">
                        {column.canSort && getSortIcon(column)}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
          {rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);
              const key = get(row, rowKey, '') || row.getRowProps().key;
              return (
                <tr {...row.getRowProps(getCustomRowProps(row))} key={key}>
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps()}
                        className={`px-4 py-6 whitespace-nowrap ${cellClassName}`}
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
        </tbody>
      </table>
    </>
  );
}

const defaultPropGetter = () => ({});

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getHeaderProps: PropTypes.func,
  getCustomRowProps: PropTypes.func,
  tableClass: PropTypes.string,
  cellClassName: PropTypes.string,
  filterOptions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.any)),
  rowKey: PropTypes.string,
};

Table.defaultProps = {
  getHeaderProps: defaultPropGetter,
  tableClass: '',
  cellClassName: '',
  filterOptions: [],
  rowKey: '',
  getCustomRowProps: () => {},
};

export default Table;
